/**
 * 使用量集計参照ページ
 *
 * usage-collector バッチが S3 daily/ に出力した企業/アカウント別の使用量集計を
 * 表形式で参照する。実測値に加え、参照時に合成されるクレジット試算（仮単価・参考）と
 * AWS 実費（参考）の列を表示する。列はバックエンドから動的に受け取る。
 */
import { useMemo, useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useUsageAggregation } from '@/lib/query/hooks/useUsageAggregation';
import { useUsageDaily } from '@/lib/query/hooks/useUsageDaily';
import type { UsageAggregationLevel } from '@/lib/types/usage/usageAggregation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DownloadIcon, HelpCircleIcon } from 'lucide-react';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

/** 一覧の列ラベル（期間集計。未知の列は列名をそのまま表示）。
 *  フロー系は期間合計、ストック系（容量・規模）は期間終了日時点の値。 */
const COLUMN_LABELS: Record<string, string> = {
  company: '企業',
  account: 'アカウント',
  accounts: '配下アカウント',
  credits_total: 'クレジット合計(参考)',
  credits_scale: 'クレジット(規模)',
  credits_capacity: 'クレジット(容量)',
  credits_flow: 'クレジット(フロー)',
  total_cost_usd: 'AWS実費USD合計',
  athena_queries: 'Athenaクエリ数',
  athena_scanned_gb: 'AthenaスキャンGB',
  athena_billed_gb: 'Athena課金対象GB',
  athena_cost_usd: 'Athena実費USD',
  athena_engine_hours: 'Athenaエンジン時間h',
  sfn_executions: 'SFN実行数',
  sfn_hours: 'SFN時間h',
  fargate_task_hours: 'Fargate推定時間h',
  fargate_cost_usd: 'Fargate実費USD',
  fargate_vcpu_hours: 'Fargate実測vCPU時',
  fargate_gb_hours: 'Fargate実測GB時',
  glue_runs: 'Glue実行数',
  glue_dpu_hours: 'Glue DPU時間',
  glue_cost_usd: 'Glue実費USD',
  sagemaker_jobs: 'SageMakerジョブ数',
  sagemaker_hours: 'SageMaker時間h',
  sagemaker_cost_usd: 'SageMaker実費USD',
  s3_gb: 'S3容量GB',
  llm_cost_usd: 'LLMコストUSD',
  llm_invocations: 'LLM実行数',
  sellers: '店舗数',
  skus: 'SKU数',
  units: '規模単位',
  aurora_gb: 'Aurora容量GB',
  redshift_gb: 'Redshift容量GB',
};

/** 日次一覧の列ラベル（窓表記なし） */
const DAILY_COLUMN_LABELS: Record<string, string> = {
  day: '日付',
  athena_queries: 'Athenaクエリ数',
  athena_scanned_gb: 'AthenaスキャンGB',
  athena_billed_gb: 'Athena課金対象GB',
  athena_cost_usd: 'Athena実費USD',
  athena_engine_hours: 'Athenaエンジン時間h',
  sfn_executions: 'SFN実行数',
  sfn_hours: 'SFN時間h',
  fargate_task_hours: 'Fargate推定時間h',
  fargate_cost_usd: 'Fargate実費USD',
  fargate_vcpu_hours: 'Fargate実測vCPU時',
  fargate_gb_hours: 'Fargate実測GB時',
  glue_runs: 'Glue実行数',
  glue_dpu_hours: 'Glue DPU時間',
  glue_cost_usd: 'Glue実費USD',
  sagemaker_jobs: 'SageMakerジョブ数',
  sagemaker_hours: 'SageMaker時間h',
  sagemaker_cost_usd: 'SageMaker実費USD',
  s3_gb: 'S3容量GB',
  llm_cost_usd: 'LLMコストUSD',
  llm_invocations: 'LLM実行数',
  sellers: '店舗数',
  skus: 'SKU数',
  units: '規模単位',
  aurora_gb: 'Aurora容量GB',
  redshift_gb: 'Redshift容量GB',
};

/** 左寄せで表示する文字列列（それ以外は数値として右寄せ・桁区切り） */
const TEXT_COLUMNS = new Set(['company', 'account', 'accounts']);

/** Athena の DB コンテキスト由来で顧客アカウントではない共有・システム DB 名。
 *  帰属バケットと同様に「共通・基盤コスト」の行として既定で非表示にする */
const UNATTRIBUTED_DB_NAMES = new Set([
  'across_company',
  'information_schema',
  'public',
  'default',
  'admin_analytics_prod',
]);

/** 特定顧客に帰属しない共通・基盤コストの行（既定で非表示）。判定キー列（account / company）が
 *  括弧付きの帰属バケット（(none) / (shared) / (unknown) / (common) 等）または上記 DB 名の行 */
const isUnattributed = (value: string) => value.startsWith('(') || UNATTRIBUTED_DB_NAMES.has(value);

/** AWS実費USD合計の対象外とする実費列。LLM は FK トークンとして別枠のため合計に含めない */
const TOTAL_COST_EXCLUDED = new Set(['llm_cost_usd']);

/** AWS 実費（試算）の合計対象列か（`*_cost_usd` のうち LLM と合計列自身を除く） */
const isTotaledCostColumn = (col: string) =>
  col.endsWith('_cost_usd') && col !== 'total_cost_usd' && !TOTAL_COST_EXCLUDED.has(col);

/** 換算列（実測の元値ではない列）か。クレジット試算と AWS 実費（合計含む）が対象。
 *  llm_cost_usd は収集した実費（実測値）なので対象外 */
const isDerivedColumn = (col: string) =>
  col.startsWith('credits_') || (col.endsWith('_cost_usd') && !TOTAL_COST_EXCLUDED.has(col));

/** 換算列のセル背景（実測列との区別用の薄い色）。本文セル用の半透明色 */
const derivedCellBg = (t: Theme) => alpha(t.palette.primary.main, 0.045);

/** 換算列のヘッダ背景。sticky ヘッダは不透明背景が必要なため、既定背景の上に
 *  同色のグラデーションを重ねる（backgroundColor を上書きしない） */
const derivedHeaderOverlay = (t: Theme) =>
  `linear-gradient(${alpha(t.palette.primary.main, 0.09)}, ${alpha(t.palette.primary.main, 0.09)})`;

/** AWS実費USD合計（total_cost_usd）列を3列目（企業・アカウント列の直後）に挿入する。
 *  実費列が無いデータ（旧スナップショット等）ではそのまま返す */
function withTotalCostColumn(
  columns: string[],
  rows: string[][],
): { columns: string[]; rows: string[][] } {
  const costIdx = columns.flatMap((c, i) => (isTotaledCostColumn(c) ? [i] : []));
  if (costIdx.length === 0) return { columns, rows };
  // 1-2列目は 企業 / アカウント（配下アカウント）。バックエンドが先頭ブロックとして
  // クレジット試算列（credits_*）を返す場合はその直後に挿入する
  let at = 2;
  while (at < columns.length && columns[at].startsWith('credits_')) at++;
  const newColumns = [...columns.slice(0, at), 'total_cost_usd', ...columns.slice(at)];
  const newRows = rows.map((r) => {
    let sum = 0;
    let has = false;
    for (const i of costIdx) {
      const v = r[i] ?? '';
      if (v === '') continue;
      const n = Number(v);
      if (!Number.isNaN(n)) {
        sum += n;
        has = true;
      }
    }
    return [...r.slice(0, at), has ? sum.toFixed(3) : '', ...r.slice(at)];
  });
  return { columns: newColumns, rows: newRows };
}

/** ヘルプダイアログの1項目。desc は本文、points は箇条書き（どちらか片方でも可） */
interface HelpItem {
  term: string;
  desc?: string;
  points?: string[];
}

/** ヘルプダイアログに表示する集計値の説明（セクション構成。判断材料として参照される
 *  ことを想定し、換算式・単価・制限を省略せずに記載する） */
const COLUMN_HELP_SECTIONS: { title: string; items: HelpItem[] }[] = [
  {
    title: 'この画面の見方',
    items: [
      {
        term: '集計期間',
        desc: '画面上部で選んだ期間（初期は直近30日）で集計します。フロー系（実行量・実費・クレジット(フロー)）は期間内の合計、ストック系（容量・規模・クレジット(規模)(容量)）は期間終了日時点の値です。',
      },
      {
        term: '列の色分け',
        desc: 'クレジット試算・AWS実費（合計含む）など、実測値から換算して作った列は薄い色付き背景で表示します。背景なしの列が収集した元の実測値です（LLMコスト USD は収集した実費なので背景なし）。',
      },
      {
        term: '空欄の意味',
        desc: 'その環境・アカウントで未計測、または期間内に実績がないことを示します（0 とは区別されます）。',
      },
      {
        term: '企業への名寄せ',
        desc: 'CommonDB の現在の紐づけ（account_settings.company_id → companies）を毎晩取得して名寄せします。集計は全期間を再生成するため、紐づけの変更は過去分にも反映されます。紐づけ未設定のアカウントは「企業名 = アカウント名」として扱います。',
      },
      {
        term: '非稼働アカウントの行',
        desc: 'CommonDB の account_settings.application_working が true でないアカウント（解約・停止中など）の行は既定で非表示です（トグルで表示。過去の使用実績自体は保持されています）。企業別では稼働アカウントを1つも持たない企業が対象です。判定は毎晩のバッチで最新化されます。',
      },
      {
        term: '帰属なし（共通・基盤）の行',
        desc: 'アカウントに帰属できなかった・特定顧客のものではない使用量で、既定では非表示です（トグルで表示）。いずれも特定の顧客ではなく「共通・基盤コスト」として読みます。',
        points: [
          '(none) = Athena でクエリの DB コンテキストも出力パスも無く、帰属先を特定できない分',
          '(shared) = 顧客専用ではない共有バケットの S3 容量（クエリ結果・中間データ・インフラ系）',
          '(unknown) = Step Functions / Glue で帰属材料が得られなかった実行',
          '(common) = 共通 DB（common）の Aurora 容量',
          'across_company / information_schema / public など、顧客アカウントではない共有・システム DB へのクエリ行も同様',
        ],
      },
    ],
  },
  {
    title: 'クレジット試算（参考・仮単価）',
    items: [
      {
        term: 'クレジット合計(参考)',
        desc: '請求イメージ「クレジット合計 = 規模 + 容量 + フロー」を仮単価で試算した参考値です（1クレジット = 1万円）。材料が未計測の成分は空欄になり、合計は値のある成分のみの和です。LLM（FKトークン）は単価未定のため含みません。',
      },
      {
        term: 'クレジット(規模) = 店舗数×SKU数の累進換算',
        desc: '規模単位（総店舗数 × 総SKU数、期間終了日時点）を累進テーブルで換算します。ベース30クレジットに〜50万規模単位を内包し、帯を超えた分にのみ帯単価が掛かります。',
        points: [
          '〜50万単位: ベース30クレジットに込み',
          '50万〜100万: 0.2円/単位',
          '100万〜500万: 0.04円/単位',
          '500万〜1,000万: 0.02円/単位',
          '1,000万〜3,000万: 0.015円/単位',
          '3,000万〜1億: 0.014円/単位',
          '1億〜: 0.0125円/単位',
        ],
      },
      {
        term: 'クレジット(容量)',
        desc: 'S3 の実容量（期間終了日時点）を 1TB = 1クレジット/月 で換算します。Aurora・Redshift の容量はクレジット計算の対象外です（実測列として表示のみ）。',
      },
      {
        term: 'クレジット(フロー)',
        desc: 'AWS実費USD合計（Athena + Glue + SageMaker + Fargate）を 150円/USD で円換算し、1万円 = 1クレジットで換算します（係数 1.0 = 原価そのまま）。',
      },
      {
        term: '前提と制限（重要）',
        points: [
          '単価はすべて料金プラン全体像 2026-07-16 時点の仮単価で、確定料金ではありません（為替 150円/USD も仮置きの固定値です）',
          'Fargate 分は SFN 実行時間からの推定値です（タスク起動オーバーヘッドで過大方向・SM 内の並列実行で過小方向の誤差。実測の課金明細ではありません）',
          'LLM（FKトークン）は単価未定のため別枠扱いで、クレジット合計にも AWS実費USD合計にも含めていません',
          '規模・容量は「月額基準の時点値」、フローは「選択期間の合計」という混在のため、直近30日程度の期間で見る前提の参考値です。期間を短くするとフロー分だけが小さくなります',
          'アカウント別表示のクレジット(規模)はアカウント単体で計算した参考値です。企業別は配下アカウントの規模単位を単純合算して1回だけテーブル適用しているため、アカウント別の値を合計しても企業別とは一致しません（ベース30クレジットが重複するため）',
        ],
      },
    ],
  },
  {
    title: 'AWS実費（参考・USD）',
    items: [
      {
        term: 'AWS実費 USD（Athena / Glue / SageMaker / Fargate）',
        desc: 'AWS 公表単価 × 使用量で算出した参考試算です。自社が AWS に支払う原価の目安であり、クレジット（顧客向け課金）とは別の値です。単価は適用開始日付きでバックエンドの単価テーブルに管理し、期間集計は日ごとに「その日の適用単価」で計算します。',
        points: [
          'Athena実費 = 課金対象GB ÷ 1000 × $5/TB（課金対象GB = 失敗クエリ・結果再利用を除外し、クエリごとの最小課金10MBを適用したスキャン量）',
          'Glue実費 = DPU時間 × $0.44/DPU時',
          'SageMaker実費 = インスタンスタイプ別の課金時間（ProcessingStartTime〜EndTime。プロビジョニング等の非課金時間を除く）× タイプ別公表単価（例: ml.m5.2xlarge $0.461/時・ml.r5.24xlarge $7.258/時）の合計。インスタンス数も実測で掛けています。課金時刻・タイプの無い旧データのみ所要時間 × 代表単価 $0.461/時で計上します',
          'Fargate実費 = 実測（Fargate実測vCPU時 × $0.04048 + Fargate実測GB時 × $0.004445）を優先。実測は ECS タスク終了イベント由来で、タスクごとの課金時間（イメージ取得開始〜終了）× 実サイズを積み上げた値です（2026-07-29 の記録開始以降の日のみ）',
          '実測の無い日（記録開始前）は従来の推定 = Fargate推定時間 × (4vCPU × $0.04048 + 8GB × $0.004445) ≈ $0.197/時 にフォールバックします。推定は SFN 実行時間ベースで4割程度の過大傾向があるため、実測と推定が混在する期間の合計は参考値として読んでください',
        ],
      },
      {
        term: 'AWS実費USD合計',
        desc: 'Athena実費 + Glue実費 + SageMaker実費 + Fargate実費 の合計です。LLMコストは FK トークンとして別枠のため含めません。実費 USD 列が将来増えた場合は自動的に合計へ含めます。Lambda など未計測分やストック系（容量・規模）は含まない参考値です。',
      },
    ],
  },
  {
    title: '使用量ディメンション（実測値）',
    items: [
      {
        term: 'Athena クエリ数 / スキャン GB / 課金対象 GB / エンジン時間',
        desc: '期間内の実行履歴メタデータの合計。結果再利用（キャッシュヒット）はスキャン 0 として数えます。課金対象GB は失敗クエリ・結果再利用を除き最小課金10MB/クエリを適用した値で、実費の換算元です（CE 実費と突合済みの基準）。収集はメタデータ API のみで、クエリ本文には触れません。',
      },
      {
        term: 'SFN 実行数 / 時間',
        desc: '期間内の Step Functions 実行回数と経過時間（開始〜終了の wall-clock）の合計。失敗・リトライ分も含みます。SF は内部で Glue / SageMaker ジョブを同期実行するため、SFN 時間にはそれらの実行時間も包含されます。',
      },
      {
        term: 'Fargate実測vCPU時 / GB時',
        desc: 'ECS タスク終了イベント（2026-07-29 記録開始）から集計した実測値。タスクごとの課金時間（イメージ取得開始〜終了）× 実際のタスクサイズ（vCPU・メモリGB）の合計で、Fargate 実費の第一の算定基礎です。帰属はタスクのパラメータ（顧客コード等）から直接判定し、判定できないシステム系タスクは (system) 等の非顧客行に集計します。',
      },
      {
        term: 'Fargate推定時間',
        desc: 'ECS（Fargate）起動型の Step Functions（データ取込・external-api 連携など）に限定した実行時間の合計です。SFN 時間の部分集合で、タスクは同期起動（runTask.sync）のため「SFN 実行時間 ≒ Fargate タスク実行時間」とみなした推定値です。SM の型（ECS 起動型か Lambda 型か）は定義ベースで毎晩自動判定し、判定の更新は過去分にも再集計で反映されます。実測（上記）の無い日の実費フォールバックに使います。',
      },
      {
        term: 'Glue 実行数 / DPU時間',
        desc: '期間内の Glue ジョブ実行。DPU時間 = 実行時間 × ワーカー構成（例: G.2X は 2 DPU/worker）。実行時間は概ね SFN 時間に包含されるため参考内訳です（SFN 時間と合算すると二重計上になります）。',
      },
      {
        term: 'SageMaker ジョブ数 / 時間',
        desc: '期間内の Processing Job 数と所要時間（作成〜終了。プロビジョニング時間込み）。所要時間は概ね SFN 時間に包含されるため参考内訳です（SFN 時間と合算すると二重計上になります）。実費はこの時間列ではなく、タイプ別のインスタンス時間（インスタンス数込み）× タイプ別公表単価で別途計算しています。',
      },
      {
        term: 'S3容量 GB',
        desc: '顧客専用バケットの期間終了日時点の実容量（CloudWatch 日次メトリクス）。共有バケットは含みません。残高のため期間合計ではありません。',
      },
      {
        term: 'Redshift容量 GB / Aurora容量 GB',
        desc: 'スキーマ（=アカウント）/ DB 別の期間終了日時点の容量（残高スナップショット）。未収集の環境では空欄になります。',
      },
      {
        term: 'LLMコスト USD / LLM実行数',
        desc: '期間内の mise の LLM 利用（トークン・原価 USD）の合計。未収集の環境では空欄になります。',
      },
      {
        term: '店舗数 / SKU数 / 規模単位',
        desc: 'マスタの期間終了日時点の COUNT。規模単位 = 総店舗数 × 総SKU数（料金案①の算定単位）。残高のため期間合計ではありません。',
      },
    ],
  },
];

function UsageColumnHelpDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <IconButton size="small" aria-label="集計値の説明">
          <HelpCircleIcon size={18} />
        </IconButton>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>集計値の説明</DialogTitle>
        </DialogHeader>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {COLUMN_HELP_SECTIONS.map((section) => (
            <Box key={section.title}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 1.5,
                  pb: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {section.items.map((h) => (
                  <Box key={h.term}>
                    <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                      {h.term}
                    </Typography>
                    {h.desc && (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {h.desc}
                      </Typography>
                    )}
                    {h.points && (
                      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                        {h.points.map((p) => (
                          <Typography
                            key={p}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.8 }}
                          >
                            {p}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const WEEKDAYS_JA = ['日', '月', '火', '水', '木', '金', '土'];

/** YYYY-MM-DD に日本語曜日を添える（例: 2026-07-23(木)）。パース不能ならそのまま返す */
function withWeekday(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  // UTC で構築してタイムゾーンによる曜日ずれを防ぐ
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return `${day}(${WEEKDAYS_JA[d.getUTCDay()]})`;
}

/** 表示中のテーブルを CSV としてダウンロードする（生値のまま・Excel 用に BOM 付き） */
function downloadCsv(
  columns: string[],
  rows: string[][],
  level: string,
  from: string | null,
  to: string | null,
) {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [columns.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))];
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `usage_${level}_${from ?? ''}_${to ?? ''}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function UsageDailyDialog({
  account,
  from,
  to,
  onClose,
}: {
  account: string;
  from: string | null;
  to: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useUsageDaily(account, from ?? undefined, to ?? undefined);
  const periodLabel = data?.from && data?.to ? `${data.from} 〜 ${data.to}` : '選択期間';
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {account} の日次使用量（{periodLabel}）
          </DialogTitle>
        </DialogHeader>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error != null && (
          <Alert severity="error">
            日次データの取得に失敗しました: {error instanceof Error ? error.message : String(error)}
          </Alert>
        )}
        {data && !isLoading && data.columns.length === 0 && (
          <Alert severity="info">
            このスナップショットには日次データがありません（バッチ更新後のスナップショットから表示されます）。
          </Alert>
        )}
        {data && data.columns.length > 0 && (
          <TableContainer sx={{ maxHeight: '60vh', overflowX: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {data.columns.map((col) => (
                    <TableCell
                      key={col}
                      align={col === 'day' ? 'left' : 'right'}
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        ...(isDerivedColumn(col) ? { backgroundImage: derivedHeaderOverlay } : {}),
                        ...(col === 'day'
                          ? {
                              position: 'sticky',
                              left: 0,
                              zIndex: 3,
                              backgroundColor: 'background.paper',
                              borderRight: '1px solid',
                              borderRightColor: 'divider',
                            }
                          : {}),
                      }}
                    >
                      {DAILY_COLUMN_LABELS[col] ?? col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row, i) => {
                  const dayIdx = data.columns.indexOf('day');
                  const dow =
                    dayIdx >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(row[dayIdx] ?? '')
                      ? new Date(`${row[dayIdx]}T00:00:00Z`).getUTCDay()
                      : -1;
                  const isWeekend = dow === 0 || dow === 6;
                  // sticky セルは不透明背景が必須。週末は行・sticky セルとも同色にする
                  const dayCellBg = isWeekend ? 'action.hover' : 'background.paper';
                  return (
                    <TableRow
                      key={i}
                      hover
                      sx={isWeekend ? { backgroundColor: 'action.hover' } : undefined}
                    >
                      {row.map((value, j) => {
                        const col = data.columns[j] ?? '';
                        return (
                          <TableCell
                            key={j}
                            align={col === 'day' ? 'left' : 'right'}
                            sx={{
                              whiteSpace: 'nowrap',
                              ...(col === 'day'
                                ? {
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    backgroundColor: dayCellBg,
                                    borderRight: '1px solid',
                                    borderRightColor: 'divider',
                                  }
                                : { fontFamily: NUMERIC_FONT_FAMILY }),
                              ...(isDerivedColumn(col) ? { backgroundColor: derivedCellBg } : {}),
                            }}
                          >
                            {col === 'day' ? withWeekday(value) : formatCell(col, value)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** 横スクロールで左固定する先頭列の固定幅（px）。left オフセットの積算に使う */
const STICKY_WIDTHS: Record<string, number> = {
  company: 140,
  account: 160,
  accounts: 240,
};

/** 先頭から連続する固定対象列だけを固定する（company/account/accounts の並びを想定） */
function stickyColumns(columns: string[]): { col: string; left: number; width: number }[] {
  const out: { col: string; left: number; width: number }[] = [];
  let left = 0;
  for (const col of columns) {
    if (!(col in STICKY_WIDTHS)) break; // 固定列は先頭に連続している前提。非固定列で打ち切り
    const width = STICKY_WIDTHS[col];
    out.push({ col, left, width });
    left += width;
  }
  return out;
}

/** 左固定セルの sx（ヘッダかどうかで z-index と背景を切替）。非固定列は undefined */
function stickyCellSx(
  col: string,
  isHeader: boolean,
  sticky: { col: string; left: number; width: number }[],
) {
  const s = sticky.find((x) => x.col === col);
  if (!s) return undefined;
  return {
    position: 'sticky' as const,
    left: s.left,
    minWidth: s.width,
    maxWidth: s.width,
    zIndex: isHeader ? 3 : 1,
    // ヘッダ(sticky上端)と重なるため不透明背景でスクロール内容を隠す
    backgroundColor: 'background.paper',
    borderRight: '1px solid',
    borderRightColor: 'divider',
  };
}

/** 数値セルのフォント。本文の DM Sans は Google Fonts 配信で等幅数字（tnum）が
 *  効かないため、同系デザインの等幅フォント DM Mono で桁を縦に揃える */
const NUMERIC_FONT_FAMILY = "'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/** 小数を持つ列（GB・時間・DPU・コスト）は小数第3位で固定して桁を揃える。
 *  クレジット列は2桁、それ以外（クエリ数・実行数・件数・店舗/SKU 数）は整数表示 */
function fractionDigitsFor(column: string): number {
  if (/^credits_/.test(column)) {
    return 2;
  }
  return /_gb(_|$)|_hours?(_|$)|_dpu_hours|_usd$|scanned_gb|latest_gb/.test(column) ? 3 : 0;
}

function formatCell(column: string, value: string): string {
  if (TEXT_COLUMNS.has(column) || value === '') {
    return value;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }
  const fd = fractionDigitsFor(column);
  return num.toLocaleString('ja-JP', { minimumFractionDigits: fd, maximumFractionDigits: fd });
}

export function UsageAggregationPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [level, setLevel] = useState<UsageAggregationLevel>('company');
  // 入力中の期間（date 欄）と、実際にクエリへ適用済みの期間を分離し、期間の変更が
  // 即時に再集計されないようにする（「適用」ボタン／プリセットで反映）
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [appliedFrom, setAppliedFrom] = useState<string>('');
  const [appliedTo, setAppliedTo] = useState<string>('');
  const [dailyAccount, setDailyAccount] = useState<string | null>(null);
  const [showUnattributed, setShowUnattributed] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, error } = useUsageAggregation({
    from: appliedFrom || undefined,
    to: appliedTo || undefined,
    level,
  });

  // AWS実費USD合計列を3列目に挿入した表示用テーブル（表と CSV の両方で使う）
  const table = useMemo(
    () => (data ? withTotalCostColumn(data.columns, data.rows) : { columns: [], rows: [] }),
    [data],
  );

  // 入力中の期間をクエリへ適用する（「適用」ボタン）
  const applyPeriod = () => {
    setAppliedFrom(from);
    setAppliedTo(to);
  };

  // プリセット（直近N日）を available_to 基準で入力欄＋適用済みの両方へ即時セットする
  const applyPreset = (days: number) => {
    const availTo = data?.available_to;
    if (!availTo) return;
    const toD = new Date(`${availTo}T00:00:00Z`);
    const fromD = new Date(toD);
    fromD.setUTCDate(fromD.getUTCDate() - (days - 1));
    let fromStr = fromD.toISOString().slice(0, 10);
    // データが窓より短い場合は実データ開始日にクランプ（無い期間を提示しない）
    const availFrom = data?.available_from;
    if (availFrom && fromStr < availFrom) fromStr = availFrom;
    setFrom(fromStr);
    setTo(availTo);
    setAppliedFrom(fromStr);
    setAppliedTo(availTo);
  };

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      // 数値列は大きい順から見たいことが多いので初回クリックは降順、文字列列は昇順
      setSortDirection(TEXT_COLUMNS.has(col) ? 'asc' : 'desc');
    }
  };

  const sticky = useMemo(() => stickyColumns(table.columns), [table]);

  // 帰属できない行（(none)/(shared)/(unknown)）は既定で隠す。
  // 判定キーは先頭の帰属列: アカウント別なら account、企業別なら company（どちらも
  // (none) 等がその列に入る）
  const keyIdx = useMemo(() => {
    const a = table.columns.indexOf('account');
    return a >= 0 ? a : table.columns.indexOf('company');
  }, [table]);

  // 稼働中の集合（CommonDB application_working=true 由来）。マスタ未生成の
  // スナップショットでは null = 絞り込みなし。帰属なしバケット（(none) 等）は
  // 専用トグルの管轄なので、非稼働判定からは除外する（二重に隠れないように）
  const workingSet = useMemo(() => {
    const arr = level === 'account' ? data?.working_accounts : data?.working_companies;
    return arr ? new Set(arr) : null;
  }, [data, level]);

  const isInactive = useMemo(
    () => (key: string) => workingSet !== null && !workingSet.has(key) && !isUnattributed(key),
    [workingSet],
  );

  const visibleRows = useMemo(() => {
    if (keyIdx < 0) return table.rows;
    return table.rows.filter((r) => {
      const key = r[keyIdx] ?? '';
      if (!showUnattributed && isUnattributed(key)) return false;
      if (!showInactive && isInactive(key)) return false;
      return true;
    });
  }, [table, showUnattributed, showInactive, keyIdx, isInactive]);

  const unattributedCount = useMemo(() => {
    if (keyIdx < 0) return 0;
    return table.rows.filter((r) => isUnattributed(r[keyIdx] ?? '')).length;
  }, [table, keyIdx]);

  const inactiveCount = useMemo(() => {
    if (keyIdx < 0) return 0;
    return table.rows.filter((r) => isInactive(r[keyIdx] ?? '')).length;
  }, [table, keyIdx, isInactive]);

  const sortedRows = useMemo(() => {
    if (!sortColumn) return visibleRows;
    const idx = table.columns.indexOf(sortColumn);
    if (idx < 0) return visibleRows;
    const isText = TEXT_COLUMNS.has(sortColumn);
    return [...visibleRows].sort((a, b) => {
      const va = a[idx] ?? '';
      const vb = b[idx] ?? '';
      if (isText) {
        const cmp = va.localeCompare(vb, 'ja');
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      const na = va === '' ? NaN : Number(va);
      const nb = vb === '' ? NaN : Number(vb);
      // 空欄・非数値は昇降に関わらず常に末尾へ
      if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
      if (Number.isNaN(na)) return 1;
      if (Number.isNaN(nb)) return -1;
      return sortDirection === 'asc' ? na - nb : nb - na;
    });
  }, [table, visibleRows, sortColumn, sortDirection]);

  if (authLoading) {
    return (
      <AppLayout title="使用量集計">
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AppLayout title="使用量集計">
        <Alert severity="warning">このページを表示するにはログインが必要です。</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="使用量集計">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          収集バッチ（usage-collector）が集計した企業・アカウント別の使用量実測値と、仮単価によるクレジット試算・AWS実費（いずれも参考値）です。試算の前提と制限は
          ? を参照してください。
        </Typography>
        <UsageColumnHelpDialog />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: data?.available_to ? 0.5 : 2, display: 'block' }}
      >
        選んだ期間で集計します（初期は直近30日）。フロー系（Athena/SFN/Glue/SageMaker/LLM）は期間内の合計、
        ストック系（S3・Aurora・Redshift 容量、店舗数・SKU数・規模）は期間終了日時点の値です。
      </Typography>
      {data?.available_to && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          データ反映: <b>{data.available_to}</b> 分まで（収集バッチは毎晩22時ごろ実行）
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="集計レベル"
          value={level}
          onChange={(e) => setLevel(e.target.value as UsageAggregationLevel)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="company">企業別</MenuItem>
          <MenuItem value="account">アカウント別</MenuItem>
        </TextField>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => applyPreset(7)}
            disabled={!data?.available_to}
          >
            直近7日
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => applyPreset(30)}
            disabled={!data?.available_to}
          >
            直近30日
          </Button>
        </Box>
        <TextField
          type="date"
          size="small"
          label="開始日"
          // 未指定時はバックエンドが実際に集計した期間（初期は直近30日）を表示する
          value={from || (data?.from ?? '')}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: data?.available_from ?? undefined,
            max: data?.available_to ?? undefined,
          }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          type="date"
          size="small"
          label="終了日"
          value={to || (data?.to ?? '')}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: data?.available_from ?? undefined,
            max: data?.available_to ?? undefined,
          }}
          sx={{ minWidth: 150 }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={applyPeriod}
          disabled={!data?.available_to}
        >
          適用
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              disabled={inactiveCount === 0}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              非稼働アカウントを表示
              {inactiveCount > 0 ? `（${inactiveCount}件）` : ''}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showUnattributed}
              onChange={(e) => setShowUnattributed(e.target.checked)}
              disabled={unattributedCount === 0}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              帰属なし(共通・基盤)を表示
              {unattributedCount > 0 ? `（${unattributedCount}件）` : ''}
            </Typography>
          }
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon size={16} />}
          disabled={!data || data.to === null || sortedRows.length === 0}
          onClick={() =>
            data && downloadCsv(table.columns, sortedRows, data.level, data.from, data.to)
          }
        >
          CSV
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          使用量集計の取得に失敗しました: {error instanceof Error ? error.message : String(error)}
        </Alert>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      )}

      {data && !isLoading && data.to === null && (
        <Alert severity="info">
          まだ収集バッチのスナップショットがありません。バッチの初回実行後に表示されます。
        </Alert>
      )}

      {data && !isLoading && data.to !== null && (
        <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {table.columns.map((col) => (
                  <TableCell
                    key={col}
                    align={TEXT_COLUMNS.has(col) ? 'left' : 'right'}
                    sx={{
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      ...(isDerivedColumn(col) ? { backgroundImage: derivedHeaderOverlay } : {}),
                      ...stickyCellSx(col, true, sticky),
                    }}
                    sortDirection={sortColumn === col ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortColumn === col}
                      direction={sortColumn === col ? sortDirection : 'desc'}
                      onClick={() => handleSort(col)}
                    >
                      {COLUMN_LABELS[col] ?? col}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row, i) => (
                <TableRow key={i} hover>
                  {row.map((value, j) => {
                    const col = table.columns[j] ?? '';
                    return (
                      <TableCell
                        key={j}
                        align={TEXT_COLUMNS.has(col) ? 'left' : 'right'}
                        sx={{
                          ...(col === 'accounts'
                            ? { overflow: 'hidden', textOverflow: 'ellipsis' }
                            : { whiteSpace: 'nowrap' }),
                          // 数値列は等幅フォントで小数点位置を縦に揃える
                          ...(TEXT_COLUMNS.has(col) ? {} : { fontFamily: NUMERIC_FONT_FAMILY }),
                          ...(isDerivedColumn(col) ? { backgroundColor: derivedCellBg } : {}),
                          ...stickyCellSx(col, false, sticky),
                        }}
                        title={col === 'accounts' ? value : undefined}
                      >
                        {col === 'account' && value ? (
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => setDailyAccount(value)}
                          >
                            {value}
                          </Link>
                        ) : (
                          formatCell(col, value)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {dailyAccount && (
        <UsageDailyDialog
          account={dailyAccount}
          from={data?.from ?? null}
          to={data?.to ?? null}
          onClose={() => setDailyAccount(null)}
        />
      )}
    </AppLayout>
  );
}
